import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ResizableMediaOptions, createResizableMediaView } from './resizable-media-utils';

export const resizableImageExtension = Node.create<ResizableMediaOptions>({
  name: 'resizable-image',

  addOptions() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HTMLAttributes: {},
      minWidth: 10,
      maxWidth: 100,
    };
  },

  group: 'block',

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('src'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('alt'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          alt: attributes.alt,
        }),
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('title'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          title: attributes.title,
        }),
      },
      width: {
        default: 100,
        parseHTML: (element: HTMLElement): number => {
          const width = element.getAttribute('data-width');
          return width ? parseInt(width, 10) : 100;
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-width': attributes.width,
          style: `width: ${attributes.width}%;`,
        }),
      },
      align: {
        default: 'left',
        parseHTML: (element: HTMLElement): string => {
          const align = element.getAttribute('data-align');
          return align || 'left';
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-align': attributes.align,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="resizable-image"]',
        getAttrs: (element: HTMLElement): Record<string, string | number> | false => {
          const img = (element as HTMLElement).querySelector('img');
          if (!img) {
            return false;
          }

          // Récupérer la largeur depuis data-width ou depuis le style
          let width = 100;
          const dataWidth = element.getAttribute('data-width');

          if (dataWidth) {
            width = parseInt(dataWidth, 10);
          } else {
            // Fallback: essayer de parser depuis le style
            const style = element.getAttribute('style');
            const widthMatch = style?.match(/width:\s*(\d+)%/);
            if (widthMatch) {
              width = parseInt(widthMatch[1], 10);
            }
          }

          // Récupérer l'alignement
          const align = element.getAttribute('data-align') || 'left';

          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt') || '',
            title: img.getAttribute('title') || '',
            width,
            align,
          };
        },
      },
      // Support pour les anciennes images sans wrapper
      {
        tag: 'img[src]',
        getAttrs: (element: HTMLElement): Record<string, string | number> => {
          const img = element as HTMLImageElement;
          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt') || '',
            title: img.getAttribute('title') || '',
            width: 100, // Largeur par défaut pour les anciennes images
            align: 'left', // Alignement par défaut pour les anciennes images
          };
        },
      },
    ];
  },

  // eslint-disable-next-line @typescript-eslint/naming-convention
  renderHTML({ HTMLAttributes }) {
    const width = HTMLAttributes['data-width'] || 100;
    const align = HTMLAttributes['data-align'] || 'left';

    let alignStyle = '';
    switch (align) {
      case 'center':
        alignStyle = 'margin: 0 auto;';
        break;
      case 'right':
        alignStyle = 'margin-left: auto;';
        break;
      case 'left':
      default:
        alignStyle = 'margin-right: auto;';
        break;
    }

    return [
      'div',
      mergeAttributes(
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-type': 'resizable-image',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-width': width,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-align': align,
          style: `width: 100%; display: block;`,
        },
        this.options.HTMLAttributes,
      ),
      [
        'img',
        mergeAttributes(
          {
            src: HTMLAttributes.src,
            alt: HTMLAttributes.alt,
            title: HTMLAttributes.title,
          },
          {
            style: `width: ${width}%; height: auto; display: block; ${alignStyle}`,
          },
        ),
      ],
    ];
  },

  addNodeView() {
    return createResizableMediaView(
      {
        nodeName: 'image',
        tagName: 'img',
      },
      this.options,
    );
  },

  addCommands() {
    return {
      setResizableImage:
        (options: { src: string; alt?: string; title?: string; width?: number; align?: string }) =>
        ({ commands }): boolean =>
          commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              alt: options.alt || '',
              title: options.title || '',
              width: options.width || 100,
              align: options.align || 'left',
            },
          }),
      updateResizableImageWidth:
        (width: number) =>
        ({ commands, state }): boolean => {
          const { from } = state.selection;
          const node = state.doc.nodeAt(from);

          if (node && node.type.name === this.name) {
            return commands.updateAttributes(this.name, { width });
          }
          return false;
        },
      updateResizableImageAlign:
        (align: string) =>
        ({ commands, state }): boolean => {
          const { from } = state.selection;
          const node = state.doc.nodeAt(from);

          if (node && node.type.name === this.name) {
            return commands.updateAttributes(this.name, { align });
          }
          return false;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('resizable-image'),
        props: {
          nodeViews: {},
        },
      }),
    ];
  },
});

// Type augmentation for commands
declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    resizableImage: {
      setResizableImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        align?: string;
      }) => ReturnType;
      updateResizableImageWidth: (width: number) => ReturnType;
      updateResizableImageAlign: (align: string) => ReturnType;
    };
  }
}
